const DEFAULT_WELCOME_MESSAGE = 'Hi there! I am your assistant. How can I help you today?';
const DEFAULT_PLACEHOLDER = 'Type your message...';
export const DEFAULT_WIDGET_FONT_FAMILY =
  'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const DEFAULT_COLORS = {
  primaryColor: '#db0a7a',
  secondaryColor: '#0081d1',
  accentColor: '#5d53ac',
  complementaryColor: '#1385dd',
};
const SIZE_PRESETS = {
  small: { width: 340, height: 520 },
  medium: { width: 404, height: 640 },
  large: { width: 480, height: 760 },
};
const FONT_SIZE_PRESETS = { small: 13, medium: 14, large: 16 };

/** Purpose: trim optional widget text. Why: display/config fields need a consistent empty-value check. */
const normalizeString = (value) => String(value || '').trim();
/** Purpose: accept only valid six-digit hex colors. Why: prevents malformed theme values from leaking into widget styles. */
const normalizeHexColor = (value, fallback) => {
  const normalized = normalizeString(value);
  if (!normalized) return fallback;

  const prefixed = normalized.startsWith('#') ? normalized : `#${normalized}`;
  return /^#[0-9a-fA-F]{6}$/.test(prefixed) ? prefixed : fallback;
};
/** Purpose: coerce and clamp a numeric widget setting. Why: user-edited dimensions and font sizes must stay within supported UI limits. */
const normalizeNumber = (value, fallback, min, max) => {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, Math.round(numeric)));
};

/** Purpose: derive a compact avatar monogram from the widget title. Why: gives the widget a readable fallback when no avatar image is configured. */
export const getAvatarText = (title = '') => {
  const source = normalizeString(title);
  if (!source) return 'VB';

  const words = source.split(/\s+/).filter(Boolean);
  const initials = words.slice(0, 2).map((word) => word[0]?.toUpperCase() || '').join('');
  return initials || source.slice(0, 2).toUpperCase();
};

/** Purpose: choose the configured agent identity text for the chat header. Why: lets a workspace show a role label, agent name, both, or neither consistently. */
export const resolveAgentIdentityText = (mode, label, agentName) => {
  const normalizedMode = ['label', 'name', 'label_name', 'hidden'].includes(mode) ? mode : 'label';
  const normalizedLabel = normalizeString(label) || 'Support Agent';
  const normalizedAgentName = normalizeString(agentName);
  if (normalizedMode === 'hidden' || !normalizedAgentName) return '';
  if (normalizedMode === 'name') return normalizedAgentName;
  if (normalizedMode === 'label_name') return `${normalizedLabel} · ${normalizedAgentName}`;
  return normalizedLabel;
};

/** Purpose: normalize persisted widget settings into a complete render-safe config. Why: older/incomplete configs need defaults and valid values before preview or runtime rendering. */
export const resolveChatWidgetConfig = (rawConfig = {}, fallbackTitle = 'Bot Preview') => {
  const title = normalizeString(rawConfig?.title) || normalizeString(fallbackTitle) || 'Bot Preview';
  const subtitle = rawConfig?.subtitle ?? '';
  const avatarText = normalizeString(rawConfig?.avatarText) || getAvatarText(title);
  const themeMode = rawConfig?.themeMode || 'light';
  const size = rawConfig?.size || 'medium';
  const sizePreset = SIZE_PRESETS[size] || SIZE_PRESETS.medium;
  const fontSize = rawConfig?.fontSize || 'medium';

  return {
    title,
    subtitle,
    avatarText,
    avatarUrl: rawConfig?.avatarUrl || '',
    showHeader: rawConfig?.showHeader !== false,
    showAvatar: rawConfig?.showAvatar !== false,
    showTitle: rawConfig?.showTitle !== false,
    showSubtitle: rawConfig?.showSubtitle !== false,
    showStatus: rawConfig?.showStatus !== false,
    showReset: rawConfig?.showReset !== false,
    showHome: rawConfig?.showHome !== false,
    statusText: normalizeString(rawConfig?.statusText) || 'Online',
    agentIdentityMode: ['label', 'name', 'label_name', 'hidden'].includes(rawConfig?.agentIdentityMode) ? rawConfig.agentIdentityMode : 'label',
    agentIdentityLabel: normalizeString(rawConfig?.agentIdentityLabel) || 'Support Agent',
    resetLabel: normalizeString(rawConfig?.resetLabel) || 'Reset',
    themeMode,
    brandColorMode: rawConfig?.brandColorMode || 'gradient',
    primaryColor: normalizeHexColor(rawConfig?.primaryColor, DEFAULT_COLORS.primaryColor),
    secondaryColor: normalizeHexColor(rawConfig?.secondaryColor, DEFAULT_COLORS.secondaryColor),
    accentColor: normalizeHexColor(rawConfig?.accentColor, DEFAULT_COLORS.accentColor),
    complementaryColor: normalizeHexColor(rawConfig?.complementaryColor, DEFAULT_COLORS.complementaryColor),
    fontStyle: rawConfig?.fontStyle || 'default',
    fontFamily: normalizeString(rawConfig?.fontFamily) || DEFAULT_WIDGET_FONT_FAMILY,
    fontUrl: rawConfig?.fontUrl || '',
    fontSize,
    textSize: normalizeNumber(rawConfig?.textSize, FONT_SIZE_PRESETS[fontSize] || 14, 12, 22),
    textColor: normalizeHexColor(rawConfig?.textColor, themeMode === 'dark' ? '#e5e7eb' : '#0f172a'),
    size,
    width: normalizeNumber(rawConfig?.width, sizePreset.width, 320, 720),
    height: normalizeNumber(rawConfig?.height, sizePreset.height, 420, 900),
    position: rawConfig?.position || 'right',
    initialState: rawConfig?.initialState || 'expanded',
    launcherMessageEnabled: rawConfig?.launcherMessageEnabled === true,
    launcherMessage: normalizeString(rawConfig?.launcherMessage).slice(0, 200),
    refreshSessionMode: rawConfig?.refreshSessionMode === 'new' ? 'new' : 'continue',
    welcomeMessage: normalizeString(rawConfig?.welcomeMessage) || DEFAULT_WELCOME_MESSAGE,
    inputPlaceholder: normalizeString(rawConfig?.inputPlaceholder) || DEFAULT_PLACEHOLDER,
    showWatermark: rawConfig?.showWatermark !== false,
  };
};

/** Purpose: build editable form state from saved widget settings. Why: keeps user-entered blanks distinct from resolved runtime defaults while preloading all controls safely. */
export const createInitialChatWidgetForm = (rawConfig = {}, fallbackTitle = '') => {
  const resolved = resolveChatWidgetConfig(rawConfig, fallbackTitle);

  return {
    title: normalizeString(rawConfig?.title) || normalizeString(fallbackTitle),
    subtitle: rawConfig?.subtitle ?? '',
    avatarText: rawConfig?.avatarText ?? '',
    avatarUrl: rawConfig?.avatarUrl || '',
    showHeader: rawConfig?.showHeader ?? resolved.showHeader,
    showAvatar: rawConfig?.showAvatar ?? resolved.showAvatar,
    showTitle: rawConfig?.showTitle ?? resolved.showTitle,
    showSubtitle: rawConfig?.showSubtitle ?? resolved.showSubtitle,
    showStatus: rawConfig?.showStatus ?? resolved.showStatus,
    showReset: rawConfig?.showReset ?? resolved.showReset,
    showHome: rawConfig?.showHome ?? resolved.showHome,
    statusText: rawConfig?.statusText ?? resolved.statusText,
    agentIdentityMode: rawConfig?.agentIdentityMode ?? resolved.agentIdentityMode,
    agentIdentityLabel: rawConfig?.agentIdentityLabel ?? resolved.agentIdentityLabel,
    resetLabel: rawConfig?.resetLabel ?? resolved.resetLabel,
    themeMode: rawConfig?.themeMode || resolved.themeMode,
    brandColorMode: rawConfig?.brandColorMode || resolved.brandColorMode,
    primaryColor: normalizeHexColor(rawConfig?.primaryColor, resolved.primaryColor),
    secondaryColor: normalizeHexColor(rawConfig?.secondaryColor, resolved.secondaryColor),
    accentColor: normalizeHexColor(rawConfig?.accentColor, resolved.accentColor),
    complementaryColor: normalizeHexColor(rawConfig?.complementaryColor, resolved.complementaryColor),
    fontStyle: rawConfig?.fontStyle || resolved.fontStyle,
    fontFamily: normalizeString(rawConfig?.fontFamily) || resolved.fontFamily,
    fontUrl: rawConfig?.fontUrl || '',
    fontSize: rawConfig?.fontSize || resolved.fontSize,
    textSize: rawConfig?.textSize ?? resolved.textSize,
    textColor: normalizeHexColor(rawConfig?.textColor, resolved.textColor),
    size: rawConfig?.size || resolved.size,
    width: rawConfig?.width ?? resolved.width,
    height: rawConfig?.height ?? resolved.height,
    position: rawConfig?.position || resolved.position,
    initialState: rawConfig?.initialState || resolved.initialState,
    launcherMessageEnabled: resolved.launcherMessageEnabled,
    launcherMessage: rawConfig?.launcherMessage ?? '',
    refreshSessionMode: rawConfig?.refreshSessionMode === 'new' ? 'new' : resolved.refreshSessionMode,
    welcomeMessage: rawConfig?.welcomeMessage ?? '',
    inputPlaceholder: rawConfig?.inputPlaceholder ?? '',
    showWatermark: rawConfig?.showWatermark ?? resolved.showWatermark,
  };
};
